import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { useState } from 'react';

import Alert from '@/components/Alert';
import Button from '@/components/Button';
import Card from '@/components/Card';
import Modal from '@/components/Modal';
import Stack from '@/components/Stack';
import { Body } from '@/components/Typography';
import { GetInvoiceDetailsDocument, useFinishDraftMutation } from '@/graphql';
import { StripeInvoice } from '@/types/graphql';

type Props = {
  invoiceId: StripeInvoice['id'];
  hideModal: () => void;
};

const ConfirmFinalizeDraft = ({ invoiceId, hideModal }: Props) => {
  const [error, setError] = useState<boolean>(false);

  const [finishDraft, { loading: isLoading }] = useFinishDraftMutation({
    refetchQueries: [
      { query: GetInvoiceDetailsDocument, variables: { invoiceId } },
    ],
  });

  const handleFinalizeDraft = async () => {
    try {
      await finishDraft({
        variables: {
          invoiceId,
        },
      });
      hideModal();
    } catch (err) {
      setError(true);
    }
  };

  return (
    <Modal
      disableClickout
      size="sm"
      title="Are you sure you want to finalize this invoice?"
      onRequestClose={hideModal}
    >
      <Card.Section>
        {error && (
          <Alert
            description="Something went wrong, please contact support"
            icon={faExclamationTriangle}
            status="danger"
            title="An error has occurred"
          />
        )}
        <Body>
          The invoice status will change to Open and cannot be edited anymore.
        </Body>
        <Stack css={{ marginTop: '10px' }} gap={10} justify="end">
          <Button
            a11yLabel="deny"
            appearance="outline"
            label="Cancel"
            status="danger"
            type="button"
            onClick={hideModal}
          />

          <Button
            a11yLabel="accept"
            isLoading={isLoading}
            label="Accept"
            type="submit"
            onClick={handleFinalizeDraft}
          />
        </Stack>
      </Card.Section>
    </Modal>
  );
};

export default ConfirmFinalizeDraft;
